<template>
  <article :id="bootcampTileId" class="bootcamp-tile">
    <div
      class="bootcamp-tile__static-wrapper"
      :style="`background-image: url(${bootcamp.background_image_url})`"
    >
      <div class="bootcamp-tile__container">
        <div
          class="bootcamp-tile__details-wrapper"
          :style="`background-image: url(${bootcamp.background_image_url})`"
        >
          <label class="bootcamp-tile__details bootcamp-tile__details--desktop">
            <span
              class="bootcamp-tile__details-item bootcamp-tile__details-item--registered"
              v-if="registered"
              >You're registered!</span
            >
            <span class="bootcamp-tile__details-item">{{
              formatDateForBootcamp
            }}</span>
            <span class="bootcamp-tile__details-item">
              <div class="bootcamp-tile__pass">
                <Passes
                  :passName="learningAreaName"
                  :laNoBorders="true"
                  :size="23"
                />
              </div>
              {{ learningAreaName }}</span
            >
          </label>
          <span class="bootcamp-tile__details-la">
            <div class="bootcamp-tile__pass ">
              <Passes
                :passName="learningAreaName"
                :laNoBorders="true"
                :size="23"
              />
            </div>
            {{ learningAreaName }}</span
          >
          <div v-if="current" class="bootcamp-tile__details-current">
            Happening Now
          </div>
          <div v-if="isNext" class="bootcamp-tile__details-current">
            Next Bootcamp
          </div>
        </div>
        <div class="bootcamp-tile__content-wrapper">
          <h4 class="bootcamp-tile__title bootcamp-tile--desktop">
            <router-link
              :to="{ name: 'bootcamp', params: { id: bootcampId } }"
              class="bootcamp-tile__title-link"
              >{{ bootcamp.name }}</router-link
            >
          </h4>
          <div class="bootcamp-tile__instructors-profile-imgs">
            <img
              v-for="instructor in limitedInstructors"
              :key="instructor.id"
              :src="getInstructorImg(instructor)"
              class="bootcamp-tile__instructor-img"
              :alt="getInstructorName(instructor)"
            />
            <div
              v-tooltip="{
                content: `${lastInstructors}`,
                placement: 'top-center',
              }"
              class="bootcamp-tile__instructor-img bootcamp-tile__instructors-profile-imgs-plus"
              v-if="
                bootcamp.instructors &&
                  bootcamp.instructors.length > instructorsOffset
              "
            >
              + {{ bootcamp.instructors.length - instructorsOffset }}
            </div>
          </div>
          <div class="bootcamp-tile__info-wrapper">
            <div class="bootcamp-tile__info">
              <h4 class="bootcamp-tile__title bootcamp-tile--mobile">
                <router-link
                  :to="{ name: 'bootcamp', params: { id: bootcampId } }"
                  class="bootcamp-tile__title-link"
                  >{{ bootcamp.name }}</router-link
                >
              </h4>
              <p class="bootcamp-tile__instructors">
                With {{ contentInstructors }}
              </p>
              <p
                class="bootcamp-tile__description"
                v-html="renderMarkdown(bootcamp.description)"
              ></p>
            </div>
            <label
              class="bootcamp-tile__details bootcamp-tile__details--mobile"
            >
              <span
                class="bootcamp-tile__details-item bootcamp-tile__details-item--registered"
                v-if="registered"
                >You're registered!</span
              >
              <span class="bootcamp-tile__details-item">{{
                formatDateForBootcamp
              }}</span>
            </label>
            <div class="bootcamp-tile__actions">
              <router-link
                v-if="!isAuthenticated"
                :to="{ name: 'login' }"
                class="bootcamp-tile__link btn btn--primary"
                >Sign in to register</router-link
              >
              <button
                v-if="!registered && isAuthenticated"
                @click="registerForBootcamp()"
                class="bootcamp-tile__link btn btn--primary"
                :disabled="loading"
                :isLoading="loading"
              >
                Register Now
              </button>
              <router-link
                :to="{ name: 'bootcamp', params: { id: bootcampId } }"
                class="bootcamp-tile__link btn btn--secondary"
                @click.native="savePosition"
                >See Details</router-link
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex'
import { format } from 'date-fns-tz'
import Passes from '@/components/properties/Passes.vue'
import { localTimezone, bootcampCommons } from '@/helpers/mixins'
import { saveCurrentElement } from '@/helpers'

export default {
  name: 'BootcampTile',
  data: () => {
    return {
      instructorsOffset: 3,
      loading: false,
    }
  },
  mixins: [localTimezone, bootcampCommons],
  components: {
    Passes,
  },
  props: {
    bootcamp: {
      type: Object,
      required: true,
    },
    renderMarkdown: {
      type: Function,
      required: true,
    },
    epoch: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
    current: {
      type: Boolean,
      required: false,
      default: false,
    },
    isNext: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      searchUserStatusBootcamps: state =>
        state.bootcamps.searchUserStatusBootcamps,
    }),
    bootcampId() {
      return this.bootcamp.id || this.bootcamp.objectID
    },
    bootcampTileId() {
      const id = this.bootcampId
      return `B${id}`
    },
    learningAreaName() {
      const {
        bootcamp: { learning_areas },
      } = this
      return learning_areas && learning_areas[0] && learning_areas[0].name
        ? learning_areas[0].name
        : 'IT Essentials'
    },
    formatDateForBootcamp() {
      const {
        bootcamp: { start_datetime: start, end_datetime: end },
        epoch,
      } = this
      const startDate = epoch ? new Date(start * 1000) : new Date(start)
      const endDate = epoch ? new Date(end * 1000) : new Date(end)
      return `${format(startDate, 'MMMM do')} - ${format(endDate, 'MMMM do')}`
    },
    userStatus() {
      const itemStatus = this.searchUserStatusBootcamps.find(
        e => e.id === this.bootcampId
      )
      return itemStatus?.user_status
    },
    registered() {
      return this.userStatus?.registered
    },
  },
  methods: {
    async registerForBootcamp() {
      try {
        this.loading = true
        await this.$store.dispatch(
          'bootcamps/registerForBootcamp',
          this.bootcampId
        )
        this.sendMixpanelEvent(this.eventsConstants.BOOTCAMP_REGISTRATION, {
          bootcamp_id: this.bootcampId,
          bootcamp_name: this.bootcamp.name,
          instructor_name: this.contentInstructors(),
          instructor_id: instructorId,
          end_datetime: this.bootcamp.end_datetime,
          start_datetime: this.bootcamp.start_datetime,
          occurrences: { ...this.flatOcurrencies() },
        })
        await this.$store.dispatch('bootcamps/getBootcampById', this.bootcampId)
      } finally {
        this.loading = false
      }
    },
    savePosition() {
      saveCurrentElement(this.bootcampTileId)
    },
  },
  mounted() {
    this.$emit('element-rend', this.bootcampTileId)
  },
}
</script>
<style lang="scss" scoped>
.bootcamp-tile {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  width: 100%;
  background-color: var(--darker-gray);
  position: relative;
  box-shadow: 0px 0px 8px #0000000a;
  border-radius: 5px;
  min-height: 290px;
  margin: 0 0 15px 0;
  &__static-wrapper {
    background: var(--black);
    width: 100%;
    display: block;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    min-height: 150px;
    border-radius: 10px;

    @include mobile {
      width: 100%;
    }
  }
  &__container {
    @include mobile {
      padding: 20px;
    }
  }
  &__details-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 20px 20px 15px 20px;
    border-radius: 10px 10px 0px 0px;
    background-size: 100%;
    background-repeat: no-repeat;

    @include mobile {
      padding: unset;
      background-image: unset !important;
    }

    @include desktop {
      padding-bottom: 18px;
    }
  }
  &__details {
    color: var(--white);
    font-size: 14px;
    line-height: 20px;
    display: flex;

    &--desktop {
      display: none;
      @include mobile {
        display: flex;
      }
    }
    &--mobile {
      display: flex;
      flex-direction: column;
      @include mobile {
        display: none;
      }
    }

    &-item {
      display: flex;
      font-size: rem(12px);
      line-height: rem(14px);
      margin-right: 10px;
      font-size: rem(14px);
      line-height: rem(20px);

      @include mobile {
        font-size: rem(14px);
        line-height: rem(20px);
        &:not(:last-child)::after {
          content: '|';
          margin-left: 10px;
          color: var(--perfect-gray);
        }
        &:not(:first-child) {
          margin-left: 10px;
        }
      }

      &--registered {
        color: var(--positive-light);
      }
    }

    &-la {
      color: var(--white);
      display: flex;
      @include mobile {
        display: none;
      }
    }

    &-current {
      background: var(--glass-pink-violet);
      padding: 5px 10px;
      border-radius: 10px 10px 10px 0px;
      font-size: 10px;
      line-height: 12px;
      color: var(--white);

      width: 90px;
      height: 22px;

      @include tablet {
        font-size: 12px;
        line-height: 14px;
        padding: 8px 15px;
        width: unset;
        height: unset;
      }
    }
  }
  &__pass {
    margin-right: 8px;
  }

  &__info-wrapper {
    color: var(--white);
    margin-top: 11px;
    display: flex;
    flex-direction: column;
    max-width: 600px;
  }
  &__info {
    margin: 0 0 30px 0;
    @include tablet {
      padding: 0;
    }
  }
  &__content-wrapper {
    padding: 0px 20px 20px 20px;
    background: var(--black);

    @include mobile {
      background: unset;
      padding: unset;
    }
  }
  &__title {
    font-family: var(--font-bold);
    font-size: rem(14px);
    line-height: rem(20px);
    padding: 0;
    margin: 0;
    width: 100%;
    color: var(--white);
    @include mobile {
      font-size: rem(24px);
      line-height: rem(32px);
    }
    @include tablet {
      max-width: 615px;
    }
    &-link {
      text-decoration: none;
      color: var(--white);
    }
    &-link:hover {
      color: color(--white);
    }
  }
  &__description {
    padding: 0;
    display: block;
    overflow-wrap: break-word;
    word-wrap: break-word;
    overflow: hidden;
    height: 5.6em;
    line-height: 1.5em;
    position: relative;

    font-size: rem(10px);
    line-height: rem(12px);
    font-weight: 450;
    a {
      color: var(--white);
      text-decoration: underline;
      &:hover {
        color: var(--orange);
      }
    }

    @include mobile {
      font-size: rem(14px);
      line-height: rem(20px);
      margin: 0 0 25px 0;
    }
  }
  &__link {
    margin-top: 10px;
    margin-right: 0;

    @include mobile {
      margin-top: auto;
    }
  }
  &__instructor-img {
    right: 0;
    border-radius: 70px;
    max-height: 70px;
    left: 30px;
    width: 50px;
  }
  &__instructors {
    font-size: rem(12px);
    line-height: rem(14px);
    font-weight: 450;
    height: 30px;
    @include mobile {
      font-size: rem(16px);
      line-height: rem(23px);
      font-weight: 500;
    }
  }
  &__instructors-profile-imgs {
    padding-top: 21px;
    display: grid;
    grid-template-columns: 40px 40px 40px 40px;
  }
  &__instructors-profile-imgs-plus {
    height: 50px;
    color: var(--white);
    background: var(--glass-mid-gray);
    background-color: var(--black);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;

    font-weight: 700;
    z-index: 2;

    left: 150px;
  }
  &__actions {
    display: flex;

    flex-direction: column-reverse;

    @include mobile {
      height: unset;
      display: flex;
      flex-direction: row;
      gap: 20px;
    }
  }
  &--mobile {
    @include mobile {
      display: none;
    }
  }
  &--desktop {
    display: none;
    @include mobile {
      display: inherit;
    }
    gap: 20px;
  }
}
</style>
